import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import PremiumLiveActivity from './PremiumLiveActivity';
import Button from '../../../../components/Button';
import type { BrandTeamSession, BrandTeamOutput, BrandTeamAgentLog } from '../../../../services/brandTeamService';

interface StepGenerateProps {
  session: BrandTeamSession;
  outputs: BrandTeamOutput[];
  agentLogs: BrandTeamAgentLog[];
  onGenerate: () => void;
  generating: boolean;
  onViewResults: () => void;
}

const StepGenerate: React.FC<StepGenerateProps> = ({ session, outputs, agentLogs, onGenerate, generating, onViewResults }) => {
  const isGenerating = session.status === 'generating';
  const isDone = session.status === 'generated';
  const hasStarted = isGenerating || isDone || outputs.length > 0;

  const planCategories: Array<Record<string, any>> = Array.isArray(session.deliverablePlan?.categories)
    ? session.deliverablePlan.categories
    : [];
  const totalExpected = planCategories.length;
  const completedCount = outputs.length;
  const progressPercent = totalExpected > 0 ? Math.round((completedCount / totalExpected) * 100) : 0;

  // Pre-generation: show credit confirmation + generate button
  if (!hasStarted) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50">
          <Sparkles className="h-8 w-8 text-brand-purple" />
        </div>
        <h3 className="text-lg font-bold text-text-dark">Ready to Generate</h3>
        <p className="mt-2 text-sm text-body-grey">
          Your plan is approved with {totalExpected} deliverables.
        </p>
        <div className="mt-2 inline-block rounded-lg bg-primary-50 px-4 py-2 text-sm font-semibold text-brand-purple">
          Estimated cost: {session.creditEstimate || '?'} credits
        </div>
        <div className="mt-8">
          <Button variant="primary" size="large" onClick={onGenerate} disabled={generating}>
            {generating ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting...</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Generate Outputs</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // During / after generation
  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-text-dark">
          {isDone ? 'Generation Complete' : 'Generating Mockups...'}
        </h2>
        <p className="mt-1 text-sm text-body-grey">
          {isDone
            ? `${completedCount} mockups generated successfully.`
            : `${completedCount} of ${totalExpected} deliverables complete`}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-text-dark">{progressPercent}% Complete</span>
          <span className="text-xs text-body-grey">{completedCount}/{totalExpected}</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-purple to-brand-blue transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {agentLogs.length > 0 && (
        <div className="mb-8">
          <PremiumLiveActivity logs={agentLogs} />
        </div>
      )}

      {/* Thumbnail strip */}
      {outputs.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-body-grey">Outputs</h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {outputs.map((output) => (
              <div key={output.id} className="h-20 w-28 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                <img src={output.outputUrl} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
            {isGenerating && (
              <div className="flex h-20 w-28 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50">
                <Loader2 className="h-5 w-5 animate-spin text-brand-purple" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Results button */}
      {isDone && (
        <div className="mt-8 flex justify-end">
          <Button variant="primary" size="large" onClick={onViewResults}>
            View Results →
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepGenerate;
